import { Presentation } from '@features/powerpoint';
import { BuildReportOptions } from './build-presentation';
import {
  SALES_ATTRIBUTION_TITLE,
  VISIT_ATTRIBUTION_TITLE,
} from '@features/executive-report/domain/slide-titles';
import {
  campaignOverviewSlide,
  campaignOverviewSlide2,
  salesDimensionsSlides,
  visitOverviewSlide,
  visitDimensionsSlides,
  salesOptimizationSummarySlide,
  campaignInsightsSlides,
} from '../slides';
import { visibleSlide } from '../../../slides/visible-slide';
import {
  agendaSlide,
  meetYourTeamSlide,
  navigatingComplexitySlide,
  incrementalityMattersSlide,
  coverSlide,
  titleSlide,
  discoverWhatsNextSlide,
  recommendationsSlide,
} from '@features/executive-report/slides';
import { headerTabs } from '../slides/header-tabs';

export type BuildReportOptionsV1 = Extract<
  BuildReportOptions,
  { designVersion: 'v1' }
>;

export const buildSlidesV1 = (
  options: BuildReportOptionsV1
): Presentation['slides'] => {
  const showSalesText = !!options.currentSalesMetrics;
  return [
    ...visibleSlide({
      slide: titleSlide(
        options.campaign,
        options.brandLogo,
        options.customFields.campaignName
      ),
      visible: options.customFields.includedSlides.intro.title,
    }),
    ...visibleSlide({
      slide: meetYourTeamSlide({
        teamMembers: options.customFields.teamMembers,
      }),
      visible: options.customFields.includedSlides.intro.meetYourTeam,
    }),
    ...visibleSlide({
      slide: agendaSlide([
        { title: 'The Who', text: ' – Audience' },
        { title: 'The What', text: ' – Creative' },
        { title: 'The Where', text: ' – Channels' },
      ]),
      visible: options.customFields.includedSlides.intro.agenda,
    }),
    ...visibleSlide({
      slide: navigatingComplexitySlide(),
      visible: options.customFields.includedSlides.intro.navigatingComplexity,
    }),
    ...visibleSlide({
      slide: incrementalityMattersSlide({
        liftText: `lift in visits${showSalesText ? ' and in-store and online sales' : ''}`,
      }),
      visible: options.customFields.includedSlides.intro.incrementalityMatters,
    }),
    ...(options.currentSalesMetrics
      ? [
          ...visibleSlide({
            slide: coverSlide({ titleText: SALES_ATTRIBUTION_TITLE }),
            visible: options.customFields.includedSlides.sales.cover,
          }),
          ...visibleSlide({
            slide: campaignOverviewSlide({
              campaign: {
                ...options.campaign,
                adSpend: options.calculatedFields.adSpend ?? 0,
              },
              report: options.currentReport,
              brandLogoWithMetadata: options.brandLogo,
              dimensionCounts: [
                options.subdimensionCounts.aud ?? 0,
                options.subdimensionCounts.channel ?? 0,
                options.subdimensionCounts.creative ?? 0,
              ],
            }),
            visible: options.customFields.includedSlides.sales.campaignOverview,
          }),
          ...visibleSlide({
            slide: campaignOverviewSlide2({
              salesMetrics: options.currentSalesMetrics,
              brandLogoWithMetadata: options.brandLogo,
              objectives: options.customFields.salesObjectives.map(
                objective => objective.value
              ),
              iroas: options.calculatedFields.incrementalReturnOnAdSpend,
            }),
            visible:
              options.customFields.includedSlides.sales.campaignOverview2,
          }),
          ...salesDimensionsSlides({
            salesMetrics: options.currentSalesMetrics,
            subdimensionsByName: options.topSalesSubdimensionsByName,
            recommendations: options.customFields.salesDimensionRecommendations,
            includedSlides: options.customFields.includedSlides.sales,
            subdimensionImages:
              options.customFields.subdimensionImagesByNameAndValue,
          }),
          ...visibleSlide({
            slide: salesOptimizationSummarySlide({
              midCampaignSalesMetrics: options.midCampaignSalesMetrics,
              currentSalesMetrics: options.currentSalesMetrics,
            }),
            visible:
              options.customFields.includedSlides.sales.optimizationSummary,
          }),
        ]
      : []),
    ...visibleSlide({
      slide: coverSlide({ titleText: VISIT_ATTRIBUTION_TITLE }),
      visible: options.customFields.includedSlides.footTraffic.cover,
    }),
    ...visibleSlide({
      slide: visitOverviewSlide({
        brandLogo: options.brandLogo,
        objectives: options.customFields.footTrafficObjectives.map(
          objective => objective.value
        ),
        visitMetrics: options.currentReport.visitMetrics,
        costPerIncrementalVisit:
          options.calculatedFields.costPerIncrementalVisit,
      }),
      visible: options.customFields.includedSlides.footTraffic.overview,
    }),
    ...visitDimensionsSlides({
      visitMetrics: options.currentReport.visitMetrics,
      subdimensionsByName: options.topVisitSubdimensionsByName,
      recommendations: options.customFields.footTrafficDimensionRecommendations,
      includedSlides: options.customFields.includedSlides.footTraffic,
      subdimensionImages: options.customFields.subdimensionImagesByNameAndValue,
    }),
    ...visibleSlide({
      slide: recommendationsSlide(
        options.customFields.recommendations.map(r => r.value),
        headerTabs('recommendations')
      ),
      visible: options.customFields.includedSlides.takeaways.recommendations,
    }),
    ...visibleSlide({
      slide: discoverWhatsNextSlide({
        observations: options.customFields.observations.map(
          observation => observation.value
        ),
      }),
      visible: options.customFields.includedSlides.takeaways.discoverWhatsNext,
    }),
    ...campaignInsightsSlides(
      options.campaignAnalyticsReport,
      options.customFields.includedSlides.campaignInsights
    ),
  ];
};
